import React, { useState } from "react";
import {
	Box,
	Typography,
	TextField,
	Button,
	Checkbox,
	FormControlLabel,
	FormHelperText,
	InputAdornment,
	IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import config from "../config.json";

const SignUp = () => {
	const navigate = useNavigate();
	const [showPassword, setShowPassword] = useState(false);

	const [formData, setFormData] = useState({
		first_name: "",
		last_name: "",
		email: "",
		password_hash: "",
		role: "user",
	});

	const [passwordError, setPasswordError] = useState("");
	const [showPasswordRequirements, setShowPasswordRequirements] =
		useState(false);

	const validatePassword = (password) => {
		const minLength = password.length >= 8;
		const hasUpperCase = /[A-Z]/.test(password);
		const hasLowerCase = /[a-z]/.test(password);
		const hasNumber = /\d/.test(password);
		const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

		let errorMessage = [];

		if (!minLength) errorMessage.push("Minimum 8 characters required");
		if (!hasUpperCase)
			errorMessage.push("At least one uppercase letter required");
		if (!hasLowerCase)
			errorMessage.push("At least one lowercase letter required");
		if (!hasNumber) errorMessage.push("At least one number required");
		if (!hasSpecialChar)
			errorMessage.push("At least one special character required");

		return {
			isValid: errorMessage.length === 0,
			errorMessage: errorMessage.join(", "),
		};
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));

		if (name === "password_hash") {
			const { isValid, errorMessage } = validatePassword(value);
			setPasswordError(errorMessage);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const { isValid } = validatePassword(formData.password_hash);
		if (!isValid) {
			alert("Please fulfill all password requirements");
			return;
		}

		const payload = {
			...formData,
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
		};

		try {
			const response = await fetch(`${config.baseURL}middlewareUsers`, {
				method: "POST",
				headers: {
					...config.headers,
					apikey: config.apikey,
					pkey: config.pkey,
				},
				body: JSON.stringify(payload),
			});

			if (response.ok) {
				const result = await response.json();
				console.log("Success:", result);
				// alert("Sign up successful!");
				navigate("/login");
			} else {
				const errorResult = await response.json();
				console.error("Error:", errorResult);
				alert(`Sign up failed: ${errorResult.message || "Please try again."}`);
			}
		} catch (error) {
			console.error("Network Error:", error);
			alert("Something went wrong. Please check your network connection.");
		}
	};

	return (
		<Box
			sx={{
				maxWidth: 400,
				mx: "auto",
				mt: 8,
				p: 3,
				boxShadow: 3,
				borderRadius: 2,
				position: "relative",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					mb: 2,
				}}
			>
				<Box
					component="img"
					src="https://res.cloudinary.com/dzu2lu2i4/image/upload/v1733212285/66b716b61e317999158e6fb8_LOGO_140_x30_1_ocp65j.png"
					alt="Company Logo"
					sx={{
						height: 30,
						width: "auto",
					}}
				/>
			</Box>

			<Typography
				variant="h4"
				component="h1"
				gutterBottom
				sx={{ textAlign: "center" }}
			>
				Sign Up
			</Typography>
			<Typography
				variant="body1"
				color="textSecondary"
				gutterBottom
				sx={{ textAlign: "center" }}
			>
				Sign up to continue
			</Typography>

			<Box component="form" sx={{ mt: 2 }} onSubmit={handleSubmit}>
				<TextField
					label="First Name"
					name="first_name"
					variant="outlined"
					fullWidth
					margin="normal"
					value={formData.first_name}
					onChange={handleChange}
					required
				/>
				<TextField
					label="Last Name"
					name="last_name"
					variant="outlined"
					fullWidth
					margin="normal"
					value={formData.last_name}
					onChange={handleChange}
					required
				/>
				<TextField
					label="Email"
					name="email"
					type="email"
					variant="outlined"
					fullWidth
					margin="normal"
					value={formData.email}
					onChange={handleChange}
					required
				/>
				<TextField
					label="Password"
					name="password_hash"
					type={showPassword ? "text" : "password"}
					variant="outlined"
					fullWidth
					margin="normal"
					value={formData.password_hash}
					onChange={handleChange}
					onFocus={() => setShowPasswordRequirements(true)}
					required
					error={!!passwordError}
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton
									aria-label="toggle password visibility"
									onClick={handleClickShowPassword}
									edge="end"
								>
									{showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
				{showPasswordRequirements && (
					<FormHelperText error={!!passwordError} sx={{ ml: 1 }}>
						{passwordError ||
							"Password requirements: Minimum 8 characters, one uppercase letter, one lowercase letter, one number, and one special character"}
					</FormHelperText>
				)}
				<Button
					type="submit"
					fullWidth
					variant="contained"
					color="primary"
					sx={{
						backgroundColor: "#000",
						"&:hover": { backgroundColor: "#333" },
						mt: 2,
					}}
				>
					SIGN UP
				</Button>
				<FormControlLabel
					control={<Checkbox />}
					label="Remember me"
					sx={{ textAlign: "left", display: "block", mt: 1 }}
				/>
			</Box>
		</Box>
	);
};

export default SignUp;
